import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Bell,
  FileText,
  AlertTriangle,
  Info,
  Plus,
  Pencil,
  Trash2,
  Download,
  X,
  Calendar,
} from "lucide-react";
import Notification from "../components/Notification";
import Reports from "../components/Reports";
import dayjs from "dayjs";
import { useTheme } from "../context/ThemeContext";
import Outliers from "../components/Outliers";
import { useDistrict } from "../utils/DistrictContext";
import {
  getNotificationMeterSettingsList,
  getAccountBasedMetersList,
  getUserNotificationSettings,
  updateUserNotificationSetting,
  addNotificationMeter,
  deleteNotificationMeter,
  updateNotificationMeter,
  getWaterUsageReport,
} from "../services";
import { toast } from "react-toastify";

export function Alerts({ onBack }) {
  const { colors } = useTheme();
  const location = useLocation();
  // Change your state declarations to this:
  const [activeTab, setActiveTab] = useState(() => location.state?.activeMainTab || "notification");
  const [outliersSubTab, setOutliersSubTab] = useState(() => location.state?.activeSubTab || "outliers");
  const [timePeriod, setTimePeriod] = useState("Past Week");
  const [minScore, setMinScore] = useState(10);
  const [dashboardWeekly, setDashboardWeekly] = useState(true);
  const [dashboardMonthly, setDashboardMonthly] = useState(true);
  const [auditMonthly, setAuditMonthly] = useState(true);
  const [auditYearly, setAuditYearly] = useState(true);
  const [mailAlert, setMailAlert] = useState("weekly");
  const [dayOfMonth, setDayOfMonth] = useState("25");
  const [showAddMeterModal, setShowAddMeterModal] = useState(false);
  const [selectedMeter, setSelectedMeter] = useState("");
  const [selectedEndDate, setSelectedEndDate] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingMeter, setEditingMeter] = useState(null);
  const [deletingMeter, setDeletingMeter] = useState(null);
  const [meterAlerts, setMeterAlerts] = useState([]);
  const user = JSON.parse(localStorage.getItem("MCData") || "{}");
  const userId = user?.userid;
  const [editEndDate, setEditEndDate] = useState("");
  const userType = user?.type;
  
  const [availableMeters, setAvailableMeters] = useState([]);
  const [metersLoading, setMetersLoading] = useState(false);
  const [metersError, setMetersError] = useState("");

  const [startMonth, setStartMonth] = useState("");
  const [endMonth, setEndMonth] = useState("");
  const [dates, setDates] = useState([null, null]);
  const [csvLoading, setCsvLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const { selectedDistrict } = useDistrict();
 
  useEffect(() => {
    if (!userId) return;
    fetchMeterAlerts();
  }, [userId, selectedDistrict]);

  useEffect(() => {
    if (!showAddMeterModal || !userId) return;
    fetchAccountBasedMeters();
  }, [showAddMeterModal, userId]);

  useEffect(() => {
    if (activeTab !== "reports" || !userId) return;
    fetchUserNotificationSettings();
  }, [activeTab, userId]);

  useEffect(() => {
    if (!showEditModal || !editingMeter) return;
    const d = editingMeter.endDate
      ? dayjs(editingMeter.endDate).format("YYYY-MM-DD")
      : "";

    setEditEndDate(d);
  }, [showEditModal, editingMeter]);

  useEffect(() => {
    if (!csvLoading) {
      setProgress(0);
      return;
    }

    let value = 0;
    const timer = setInterval(() => {
      value = Math.min(value + 15, 90);
      setProgress(value);
    }, 400);

    return () => clearInterval(timer);
  }, [csvLoading]);

  useEffect(() => {
  // Check if we have incoming instructions from the back button
  if (location.state?.activeMainTab === "outliers") {
    setActiveTab("outliers");
    
    if (location.state?.activeSubTab) {
      setOutliersSubTab(location.state.activeSubTab);
    }

    // This part is CRITICAL: Wipe the state from history so the 
    // tabs don't "stick" on outliers forever.
    window.history.replaceState({}, document.title);
  }
}, [location.state]);
  const fetchUserNotificationSettings = async () => {
    try {
      const res = await getUserNotificationSettings(userId);

      const settings = res?.data;

      if (!settings) return;

      setDashboardWeekly(!!settings.dashboard_weekly);
      setDashboardMonthly(!!settings.dashboard_monthly);

      setAuditMonthly(!!settings.audit_report_monthly);

      // Backend gives weekly, UI shows yearly
      setAuditYearly(!!settings.audit_report_weekly);

      setMailAlert(settings.notification_interval || "none");
    } catch (error) {
      console.error("Failed to fetch user notification settings", error);
    }
  };

  // const handleNotificationToggle = async (option, value) => {
  //   try {
  //     await updateUserNotificationSetting(userId, option, value);
  //     console.log("Notification updated:", option, value);
  //   } catch (error) {
  //     console.error("Failed to update notification setting", error);

  //     // Optional: revert UI state on failure
  //     fetchUserNotificationSettings();
  //   }
  // };
  const handleNotificationToggle = async (option, value) => {
    try {
      if (option === "notification_interval") {
        const types =
          value === "daily" ? "daily" : value === "weekly" ? "weekly" : "None"; // IMPORTANT: capital N

        await updateUserNotificationSetting(
          userId,
          "meter_status",
          true,
          types,
        );

        toast.success(`Mail alert set to ${types}`);
      } else {
        await updateUserNotificationSetting(userId, option, value);
        toast.success("Notification preference updated");
      }
    } catch (error) {
      console.error("Failed to update notification", error);
      toast.error("Failed to update notification");
      fetchUserNotificationSettings();
    }
  };

  const fetchMeterAlerts = async () => {
    try {
      const res = await getNotificationMeterSettingsList(userId);

      const formatted = res.data.map((item) => ({
        meter: item.meter_id,
        accountName: item.account_name,
        endDate: item.End_Date,
        district: item.district,
        meter_id: item.meter_id,
        register_number: item.register_number,
      }));

      setMeterAlerts(formatted);
    } catch (error) {
      console.error("Failed to fetch meter alerts", error);
    }
  };

  const fetchAccountBasedMeters = async () => {
    try {
      setMetersLoading(true);
      const res = await getAccountBasedMetersList(userId);
      setAvailableMeters(res?.data || []);
    } catch (error) {
      console.error("Failed to fetch account based meters", error);
      setMetersError("Unable to load meters");
    } finally {
      setMetersLoading(false);
    }
  };

  const handleAddMeter = async () => {
    try {
      if (!selectedMeter || !selectedEndDate) {
        toast.error("Please select meter and end date");
        return;
      }

      const [meter_id, register_number] = selectedMeter.split("-");

      const selectedObj = availableMeters.find(
        (m) =>
          String(m.meter_id) === meter_id &&
          String(m.register_number || "") === (register_number || ""),
      );

      if (!selectedObj) {
        toast.error("Invalid meter selected");
        return;
      }

      await addNotificationMeter({
        user_id: userId,
        weekly: dashboardWeekly,
        monthly: dashboardMonthly,
        meter_id,
        account_name: selectedObj.account_name,
        end_date: selectedEndDate,
        register_number: register_number || "",
      });

      await fetchMeterAlerts();

      setSelectedMeter("");
      setSelectedEndDate("");

      setShowAddMeterModal(false);
      toast.success("Meter added successfully");
    } catch (err) {
      console.error("Add meter failed", err);
      toast.error("Failed to add meter");
    }
  };

  const handleUpdateMeter = async () => {
    try {
      if (!editingMeter) return;

      if (!editEndDate) {
        toast.error("Please select end date");
        return;
      }

      await updateNotificationMeter({
        user_id: userId,
        weekly: dashboardWeekly,
        monthly: dashboardMonthly,
        meter_id: editingMeter.meter_id,
        account_name: editingMeter.accountName,
        end_date: `${editEndDate} 00:00:00`,
        register_number: editingMeter.register_number || "",
      });

      await fetchMeterAlerts();
      setShowEditModal(false);
      setEditingMeter(null);
      setEditEndDate("");
      toast.success("Meter updated successfully");
    } catch (err) {
      console.error("Update meter failed", err);
      alert("Failed to update meter");
      toast.error("Failed to update meter");
    }
  };

  const handleDeleteMeter = async () => {
    try {
      if (!deletingMeter) return;

      await deleteNotificationMeter({
        user_id: userId,
        meter_id: deletingMeter.meter_id,
        register_number: deletingMeter.register_number || "",
      });

      await fetchMeterAlerts();
      setShowDeleteModal(false);
      setDeletingMeter(null);
      toast.success("Meter deleted successfully");
    } catch (err) {
      console.error("Delete meter failed", err);
      toast.error("Failed to delete meter");
    }
  };

  const downloadWaterUsageReport = async () => {
    if (!startMonth || !endMonth || !dayOfMonth) {
      alert("Please select start month, end month and day of month");
      return;
    }

    try {
      setCsvLoading(true);

      const res = await getWaterUsageReport({
        start_date: startMonth,
        end_date: endMonth,
        aggregate_on: dayOfMonth,
      });

      const data = res?.data;

      if (!data || data.length === 0) {
        alert("No data found for selected range");
        return;
      }

      const headers = data[0];
      const rows = data.slice(1);

      const escapeValue = (value) => {
        if (typeof value === "string" && value.includes(",")) {
          return `"${value}"`;
        }
        return value;
      };

      const csvContent = [headers, ...rows]
        .map((row) => row.map(escapeValue).join(","))
        .join("\n");

      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `Report_${startMonth}_to_${endMonth}_aggregated_by_${dayOfMonth}_of_month.csv`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Water usage download failed", err);
      alert("Failed to download report");
    } finally {
      setProgress(100);
      setTimeout(() => setProgress(0), 800);
      setCsvLoading(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <motion.div
        className="mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1
          className="text-4xl font-bold text-transparent bg-clip-text"
          style={{
            backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Reports & Alerts
        </h1>
        <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>
          Monitor system notifications and data outliers
        </p>
      </motion.div>

      {/* Tab Controls */}
      <motion.div
        className="flex gap-8 mb-6 border-b flex-wrap"
        style={{ borderColor: colors.border }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        {[
          { id: "notification", label: "Notification", icon: Bell },
          ...(userType !== "U"
            ? [{ id: "reports", label: "Reports and Alerts", icon: FileText }]
            : []),
          { id: "outliers", label: "Data Outliers", icon: AlertTriangle },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="relative pb-3 px-2 transition-colors duration-300"
            style={{
              color:
                activeTab === tab.id ? colors.primary : colors.textSecondary,
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.id)
                e.currentTarget.style.color = colors.text;
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.id)
                e.currentTarget.style.color = colors.textSecondary;
            }}
          >
            <div className="flex items-center gap-2">
              <tab.icon className="w-4 h-4" />
              <span className="font-semibold">{tab.label}</span>
            </div>
            {activeTab === tab.id && (
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-0.5"
                layoutId="activeTab"
                transition={{ duration: 0.3 }}
                style={{
                  background: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
                }}
              />
            )}
          </button>
        ))}
      </motion.div>

      {/* Content Area */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {activeTab === "notification" && <Notification />}

        {activeTab === "reports" && userType !== "U" && (
          <Reports
            dashboardWeekly={dashboardWeekly}
            setDashboardWeekly={setDashboardWeekly}
            dashboardMonthly={dashboardMonthly}
            setDashboardMonthly={setDashboardMonthly}
            auditMonthly={auditMonthly}
            setAuditMonthly={setAuditMonthly}
            auditYearly={auditYearly}
            setAuditYearly={setAuditYearly}
            dayOfMonth={dayOfMonth}
            setDayOfMonth={setDayOfMonth}
            meterAlerts={meterAlerts}
            setShowAddMeterModal={setShowAddMeterModal}
            setEditingMeter={setEditingMeter}
            setDeletingMeter={setDeletingMeter}
            setShowDeleteModal={setShowDeleteModal}
            showDeleteModal={showDeleteModal}
            showAddMeterModal={showAddMeterModal}
            selectedMeter={selectedMeter}
            setSelectedMeter={setSelectedMeter}
            selectedEndDate={selectedEndDate}
            setSelectedEndDate={setSelectedEndDate}
            availableMeters={availableMeters}
            metersLoading={metersLoading}
            metersError={metersError}
            mailAlert={mailAlert}
            setMailAlert={setMailAlert}
            onToggleNotification={handleNotificationToggle}
            onAddMeter={handleAddMeter}
            onDeleteMeter={handleDeleteMeter}
            showEditModal={showEditModal}
            setShowEditModal={setShowEditModal}
            editingMeter={editingMeter}
            editEndDate={editEndDate}
            setEditEndDate={setEditEndDate}
            onUpdateMeter={handleUpdateMeter}
            startMonth={startMonth}
            setStartMonth={setStartMonth}
            endMonth={endMonth}
            setEndMonth={setEndMonth}
            dates={dates}
            setDates={setDates}
            onDownloadWaterReport={downloadWaterUsageReport}
            csvLoading={csvLoading}
            progress={progress}
          />
        )}

        {activeTab === "outliers" && (
          <Outliers
            outliersSubTab={outliersSubTab}
            setOutliersSubTab={setOutliersSubTab}
            timePeriod={timePeriod}
            setTimePeriod={setTimePeriod}
            minScore={minScore}
            setMinScore={setMinScore}
          />
        )}
      </motion.div>

      {/* Add Meter Modal */}
    </div>
  );
}

export default Alerts;
